import { useState, useEffect } from 'react';
import type { OnboardingState } from '../types';

const ONBOARDING_STORAGE_KEY = 'simplabooka_onboarding';

const DEFAULT_ONBOARDING_STATE: OnboardingState = {
  userType: null,
  nativeLanguage: null,
  targetLanguage: null,
  cefrLevel: null,
  completed: false,
};

/**
 * Hook for managing onboarding state in localStorage
 * Used for pre-login onboarding flow
 */
export function useOnboardingState() {
  const [onboardingState, setOnboardingState] = useState<OnboardingState>(() => {
    // Initialize from localStorage
    try {
      const stored = localStorage.getItem(ONBOARDING_STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error reading onboarding state from localStorage:', error);
    }
    return DEFAULT_ONBOARDING_STATE;
  });

  // Persist to localStorage whenever state changes (except for completion, which is handled manually)
  useEffect(() => {
    // Skip auto-save if we're in the middle of completing
    // (completeOnboarding handles its own save to prevent race conditions)
    try {
      const currentStored = localStorage.getItem(ONBOARDING_STORAGE_KEY);
      if (currentStored) {
        const stored = JSON.parse(currentStored);
        // Don't overwrite if localStorage already has completed=true
        if (stored.completed && !onboardingState.completed) {
          return;
        }
      }
      localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(onboardingState));
    } catch (error) {
      console.error('Error saving onboarding state to localStorage:', error);
    }
  }, [onboardingState]);

  const updateOnboarding = (updates: Partial<OnboardingState>) => {
    const newState = { ...onboardingState, ...updates };
    setOnboardingState(newState);
    // Save immediately to localStorage
    try {
      localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(newState));
    } catch (error) {
      console.error('Error saving onboarding update to localStorage:', error);
    }
  };

  const completeOnboarding = () => {
    // Calculate new state directly (not using functional form due to closure issues)
    const newState = { ...onboardingState, completed: true };
    
    // Save to localStorage FIRST (synchronously)
    try {
      localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(newState));
    } catch (error) {
      console.error('Error saving onboarding completion to localStorage:', error);
    }
    
    // Then update React state (async)
    setOnboardingState(newState);
  };

  const resetOnboarding = () => {
    setOnboardingState(DEFAULT_ONBOARDING_STATE);
    localStorage.removeItem(ONBOARDING_STORAGE_KEY);
  };

  // These methods read from React state (for UI reactivity)
  // For critical timing, use the exported functions below that read from localStorage
  const hasCompletedOnboarding = (): boolean => {
    return onboardingState.completed;
  };

  const isOnboardingStarted = (): boolean => {
    return !!(
      onboardingState.userType ||
      onboardingState.nativeLanguage ||
      onboardingState.targetLanguage ||
      onboardingState.cefrLevel
    );
  };

  return {
    onboardingState,
    updateOnboarding,
    completeOnboarding,
    resetOnboarding,
    hasCompletedOnboarding,
    isOnboardingStarted,
  };
}

/**
 * Get onboarding state from localStorage (single source of truth)
 * Use this when you need the latest value immediately
 */
export function getOnboardingState(): OnboardingState | null {
  try {
    const stored = localStorage.getItem(ONBOARDING_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error reading onboarding state:', error);
  }
  return null;
}

/**
 * Check if onboarding has been completed (reads from localStorage)
 * Use this when timing matters (e.g., in event handlers)
 */
export function hasCompletedOnboarding(): boolean {
  const stored = getOnboardingState();
  return stored?.completed ?? false;
}

/**
 * Check if onboarding has been started (reads from localStorage)
 * Use this when timing matters (e.g., in event handlers)
 */
export function isOnboardingStarted(): boolean {
  const stored = getOnboardingState();
  if (!stored) return false;
  return !!(
    stored.userType ||
    stored.nativeLanguage ||
    stored.targetLanguage ||
    stored.cefrLevel
  );
}

/**
 * Clear onboarding state (used after transferring to user account)
 */
export function clearOnboardingState(): void {
  try {
    localStorage.removeItem(ONBOARDING_STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing onboarding state:', error);
  }
}

